/**
 * A tela SENATRAN: só o que a Operação já aprovou — enviar, acompanhar o retorno, não perder
 * prazo (mockup da Gisele, tela 1).
 *
 * **O universo é o recorte `senatran`** (`validacao = 'APROVADO'`). Trânsito que ninguém
 * conferiu não aparece aqui, e mesmo que aparecesse o envio seria recusado — pela rota e
 * pela constraint `envio_exige_validacao`. A tela e o banco dizem a mesma coisa.
 *
 * **KPI e filtro são a mesma régua** (§4.1), agora com os cartões do mockup: aguardando
 * envio, com erro, prazo crítico de 24 h e de 7 dias, enviados hoje, em processamento,
 * cancelados. Sete, e não seis: a exceção ao teto do §3 é pedido do cliente e está anotada
 * lá. Os filtros em abas (Todos, Aguardando, Em processamento, Pendentes de pagamento, Com
 * erro, Reclassificação TAG) acendem **os mesmos** recortes — é outro atalho para o mesmo
 * estado, não um segundo filtro somado ao primeiro.
 *
 * **Chegando de Indicadores** (`?recorte=…`, `O-15`), o universo passa a ser o do período
 * inteiro, e não só o dos aprovados: o drill-down precisa abrir exatamente o que o
 * indicador contou (`AC14`), e um indicador de "não enviáveis" conta justamente o que
 * nunca foi aprovado.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { avisarQueOCenarioPodeTerMudado } from "../api/cenario";
import {
  EVENTO_DA_CONTINGENCIA,
  avisarQueAContingenciaPodeTerMudado,
} from "../api/contingencia";
import { useSessao } from "../api/contexto-sessao";
import { ErroDaApi, api } from "../api/cliente";
import {
  APRESENTACAO_DA_ACAO,
  APRESENTACAO_DO_ENVIO,
  APRESENTACAO_DO_STATUS,
  ehDemonstracao,
} from "../api/tipos";
import type {
  Consultado,
  Enviado,
  ItemDaFila,
  PrazoAoVivo,
} from "../api/tipos";
import { Badge, Botao, Estado, Kpi, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { RegimeDePrazos } from "../componentes/RegimeDePrazos";
import { RoteiroDaDemonstracao } from "../componentes/RoteiroDaDemonstracao";
import { dataCurta, reais, restante, rotuloDoPrazo } from "../formato";
import { useCelular } from "../ganchos/useCelular";
import { useContingenciaDaMoldura } from "../ganchos/useContingencia";
import { consultaDaExportacao, useFila } from "../ganchos/useFila";
import { usePerdidosHoje, useProximosPrazos } from "../ganchos/usePrazos";
import { useTransito } from "../ganchos/useTransito";
import { DetalheDoTransito } from "./DetalheDoTransito";
import { LinkDasNotificacoes } from "./NotificacoesInternas";
import { Chip, Paginacao } from "./Operacao";
import { PerdaDoDia } from "./PerdaDoDia";
import {
  CARTOES_DA_SENATRAN,
  ORIGEM_DO_RECORTE,
  ROTULO_DO_RECORTE,
  detalheDoPrazo,
} from "./reguas";
import { agoraDoInquilino } from "../relogio";
import {
  controlaADemonstracao,
  eRegulador,
  supervisiona,
} from "../componentes/navegacao";

const LIMITE = 50;
/** No celular, vinte por página: cinquenta cartões eram seis telas de rolagem antes do
 *  detalhe do envio. */
const LIMITE_NO_CELULAR = 20;

/** O nome do prazo numa coluna estreita. O completo está no detalhe. */
const PRAZO_CURTO: Record<string, string> = {
  ENVIO_24H: "24 h",
  PROCESSAMENTO_7D: "7 dias",
  AUTUACAO_30D: "30 dias",
  CNH_8D: "CNH 8 d",
  CNH_15D: "CNH 15 d",
  PAGAMENTO_CONTRATUAL: "Contrato",
  ENVIO_AO_AUTUADOR: "Autuador",
};

/** As abas do mockup. `null` é a que ainda não tem recorte (depende da `R-07`). */
const ABAS: { rotulo: string; chave: string | null }[] = [
  { rotulo: "Todos", chave: "senatran" },
  { rotulo: "Aguardando", chave: "aguardando_envio" },
  { rotulo: "Aguardando retorno", chave: "aguardando_retorno" },
  { rotulo: "Em processamento", chave: "em_processamento" },
  { rotulo: "Pendentes de pagamento", chave: "aguardando_pagamento" },
  { rotulo: "Com erro", chave: "com_erro_de_envio" },
  { rotulo: "Reclassificação TAG", chave: null },
];

/** As abas da contingência (`R-06`): só aparecem quando há o que mostrar, ou quando a faixa ou
 *  o alerta trouxeram até elas. */
const ABAS_DA_CONTINGENCIA = [
  { rotulo: "Na contingência", chave: "na_fila_da_contingencia" },
  { rotulo: "Venceram na contingência", chave: "venceram_na_contingencia" },
];

type CenaDaSenatran = "SENATRAN_24H" | "SENATRAN_7D";

/** As colunas que o servidor ordena por índice (`O-08`, migração `0032`). */
type ColunaOrdenavel = "placa" | "passagem" | "envio";

function CabecalhoOrdenavel({
  coluna,
  rotulo,
  ordem,
  direcao,
  aoOrdenar,
}: {
  coluna: ColunaOrdenavel;
  rotulo: string;
  ordem: string | null;
  direcao: "asc" | "desc";
  aoOrdenar: (coluna: ColunaOrdenavel) => void;
}) {
  const ativa = ordem === coluna;
  return (
    <th
      aria-sort={
        ativa ? (direcao === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 uppercase hover:text-tinta"
        onClick={() => aoOrdenar(coluna)}
      >
        {rotulo}
        <span aria-hidden="true">
          {ativa ? (direcao === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

export function Senatran() {
  const [parametros, definirParametros] = useSearchParams();
  const recorteDeIndicadores = parametros.get("recorte");
  const universo = recorteDeIndicadores ? "todos" : "senatran";
  const cartao = parametros.get("cartao") ?? universo;
  const [pagina, definirPagina] = useState(0);
  const [selecionado, definirSelecionado] = useState<string | null>(null);
  const celular = useCelular();
  const limite = celular ? LIMITE_NO_CELULAR : LIMITE;
  const [marcados, definirMarcados] = useState<Set<string>>(new Set());
  const [lote, definirLote] = useState<{
    texto: string;
    cor: "verde" | "amarelo";
  } | null>(null);
  const [emLote, definirEmLote] = useState(false);
  // Quantos já foram, no envio em lote (tarefa 20): com trinta marcados, "Processando…"
  // parado por alguns segundos parecia a tela travada.
  const [andamento, definirAndamento] = useState<{
    tipo: "enviar" | "consultar";
    feitos: number;
    total: number;
  } | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);
  const { quem } = useSessao();
  const podeSimular = controlaADemonstracao(quem);
  // O poder concedente lê a fila e o detalhe (`OB-13`): sem envio, sem exportação em massa e
  // sem marcar — o servidor recusa as três, e a tela não as oferece.
  const somenteLeitura = eRegulador(quem?.perfil);
  const [simulando, definirSimulando] = useState<CenaDaSenatran | null>(null);
  // O regime de prazos de cada concessão (ADR-057): fechado por padrão, aberto pelo botão.
  const [verRegime, definirVerRegime] = useState(false);
  // O roteiro da demonstração (`O-10`): os dez cenários e quais já aconteceram — o modo livre.
  const [verRoteiro, definirVerRoteiro] = useState(false);

  const filtros = useMemo(() => {
    const p = new URLSearchParams();
    for (const [chave, valor] of parametros)
      if (chave !== "cartao") p.append(chave, valor);
    return p.toString();
  }, [parametros]);

  const fila = useFila({
    universo,
    cartoes: [
      ...CARTOES_DA_SENATRAN.map((c) => c.chave),
      // As abas que não são cartão também levam o número.
      "em_processamento",
      "aguardando_pagamento",
      ...ABAS_DA_CONTINGENCIA.map((a) => a.chave),
      universo,
    ],
    cartao,
    filtros,
    pagina,
    limite,
  });
  const transito = useTransito(selecionado, !somenteLeitura);
  // A fila da contingência mudou (o *Tentar agora* da faixa, um envio que ficou nela): relê.
  const contingencia = useContingenciaDaMoldura();
  const { recarregar: recarregarFila } = fila;
  useEffect(() => {
    const aoAvisar = () => void recarregarFila();
    window.addEventListener(EVENTO_DA_CONTINGENCIA, aoAvisar);
    return () => window.removeEventListener(EVENTO_DA_CONTINGENCIA, aoAvisar);
  }, [recarregarFila]);
  const proximos = useProximosPrazos(filtros, fila.atualizadoEm);
  const perdidos = usePerdidosHoje(fila.atualizadoEm);

  useEffect(() => definirPagina(0), [cartao, filtros]);
  useEffect(() => definirMarcados(new Set()), [cartao, filtros, pagina]);

  const itens = useMemo(() => fila.itens ?? [], [fila.itens]);
  useEffect(() => {
    if (itens.length === 0) definirSelecionado(null);
    else if (!selecionado || !itens.some((t) => t.id === selecionado))
      definirSelecionado(itens[0]!.id);
  }, [itens, selecionado]);

  function acender(chave: string) {
    const p = new URLSearchParams(parametros);
    if (chave === universo) p.delete("cartao");
    else p.set("cartao", chave);
    definirParametros(p);
  }

  function marcar(id: string) {
    definirMarcados((m) => {
      const novo = new Set(m);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  // Recebido e aguardando o retorno não se reenvia — seria o mesmo evento; consulta-se.
  const enviaveis = itens.filter(
    (t) =>
      marcados.has(t.id) &&
      t.status === "CAPTURADO" &&
      t.validacao === "APROVADO" &&
      t.retorno_senatran !== "AGUARDANDO" &&
      // Já na fila da contingência (`R-06`): sai sozinho quando a SENATRAN voltar.
      t.situacao_envio !== "NA_FILA",
  );
  const consultaveis = itens.filter(
    (t) =>
      marcados.has(t.id) &&
      (t.status === "EM_PROCESSAMENTO" || t.retorno_senatran === "AGUARDANDO"),
  );

  /**
   * Um por vez, e não em paralelo: é a mesma rota do envio individual, com a mesma trava e a
   * mesma trilha — e a SERPRO de verdade tem limite de taxa. O resumo diz quantos foram e
   * quantos não, com o motivo do primeiro que falhou: "3 de 5" sem o porquê não ajuda.
   */
  async function emMassa(tipo: "enviar" | "consultar") {
    const alvos = tipo === "enviar" ? enviaveis : consultaveis;
    definirEmLote(true);
    definirLote(null);
    let certos = 0;
    let naFila = 0;
    let primeiraFalha: string | null = null;
    let feitos = 0;
    definirAndamento({ tipo, feitos, total: alvos.length });
    for (const t of alvos) {
      try {
        const r = await api<Enviado | Consultado>(
          `/tollvision/transitos/${t.id}/${tipo}`,
          {
            method: "POST",
            corpo: {},
          },
        );
        // A SENATRAN não respondeu (`R-06`): o envio ficou na fila da contingência — não é
        // falha, e o resto do lote vai direto para a fila, sem esperar por ela.
        if ("na_fila_desde" in r && r.na_fila_desde) naFila += 1;
        else certos += 1;
      } catch (falha) {
        primeiraFalha ??= `${t.placa ?? t.id}: ${falha instanceof ErroDaApi ? falha.message : "falha"}`;
      }
      feitos += 1;
      definirAndamento({ tipo, feitos, total: alvos.length });
    }
    definirAndamento(null);
    definirLote({
      texto:
        `${tipo === "enviar" ? "Enviados" : "Consultados"} ${certos} de ${alvos.length}.` +
        (naFila
          ? ` ${naFila} na fila da contingência: a SENATRAN não respondeu, e ${naFila === 1 ? "ele sai sozinho" : "eles saem sozinhos"} quando ela voltar.`
          : "") +
        (primeiraFalha ? ` Primeira falha — ${primeiraFalha}` : ""),
      cor: primeiraFalha || naFila ? "amarelo" : "verde",
    });
    definirEmLote(false);
    definirMarcados(new Set());
    avisarQueOCenarioPodeTerMudado();
    if (naFila) avisarQueAContingenciaPodeTerMudado();
    await Promise.all([fila.recarregar(), transito.recarregar()]);
  }

  /** O-08: a busca e a ordem moram na URL, como os filtros — a régua, a fila, os próximos
   *  prazos e a exportação leem o mesmo conjunto. */
  const busca = parametros.get("busca") ?? "";
  const [digitada, definirDigitada] = useState(busca);
  useEffect(() => definirDigitada(busca), [busca]);
  function buscar(texto: string) {
    const p = new URLSearchParams(parametros);
    if (texto.trim()) p.set("busca", texto.trim());
    else p.delete("busca");
    definirParametros(p);
  }
  const ordem = parametros.get("ordem");
  const direcao = parametros.get("direcao") === "asc" ? "asc" : "desc";
  function ordenar(coluna: ColunaOrdenavel) {
    const p = new URLSearchParams(parametros);
    if (ordem === coluna && direcao === "asc") {
      p.set("direcao", "desc");
    } else if (ordem === coluna) {
      // Terceiro clique: volta para a ordem de trabalho do cartão.
      p.delete("ordem");
      p.delete("direcao");
    } else {
      p.set("ordem", coluna);
      p.set("direcao", "asc");
    }
    definirParametros(p);
  }
  const exportacao = (formato: "csv" | "json") =>
    `/tollvision/transitos/exportar?${consultaDaExportacao({ filtros, universo, cartao }, formato)}`;

  /**
   * Tarefa 11: os dois cartões de prazo crítico ficavam em zero, porque a massa da
   * demonstração nunca cai nas janelas deles. Cada botão grava **um** trânsito que cai — o
   * aprovado a minutos das 24 h, ou o em processamento a minutos dos 7 dias — e acende o
   * cartão. O vencimento é o do motor de prazos, de verdade.
   */
  async function simularPrazo(cena: CenaDaSenatran) {
    definirSimulando(cena);
    try {
      const r = await api<PrazoAoVivo>("/demonstracao/prazo-ao-vivo", {
        method: "POST",
        corpo: { minutos: 3, cena },
      });
      const p = new URLSearchParams(parametros);
      p.set(
        "cartao",
        cena === "SENATRAN_24H" ? "prazo_24h_critico" : "prazo_7d_critico",
      );
      definirParametros(p);
      definirSelecionado(r.transito_id);
      const valor = reais(r.valor_centavos);
      definirAviso(
        cena === "SENATRAN_24H"
          ? `${r.placa} foi aprovado pela operação e as 24 h vencem em 3 minutos. Enviado a tempo, é ${valor} arrecadado; se ninguém enviar, é perda.`
          : `${r.placa} está em processamento há quase 7 dias. Em 3 minutos a SENATRAN o cancela: sem desfecho informado, ${valor} viram perda.`,
      );
      await fila.recarregar();
    } catch (falha) {
      definirLote({
        texto:
          falha instanceof ErroDaApi
            ? falha.message
            : "Não foi possível simular o prazo.",
        cor: "amarelo",
      });
    } finally {
      definirSimulando(null);
    }
  }

  const regua = fila.regua;
  const todosMarcados =
    itens.length > 0 && itens.every((t) => marcados.has(t.id));

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            SENATRAN
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            Envio, retorno e prazos dos trânsitos já validados pela operação
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* A perda do dia, como na Operação (05/10): o que nem a SENATRAN nem a Operação
              salvaram a tempo. O clique abre a lista aqui mesmo, como recorte. */}
          <PerdaDoDia perdidos={perdidos} />
          {podeSimular && (
            <>
              <Botao
                papel="atencao"
                disabled={simulando !== null}
                title="Demonstração: grava um trânsito aprovado cuja passagem foi há quase 24 h. O prazo vence de verdade, pelo motor de prazos."
                onClick={() => void simularPrazo("SENATRAN_24H")}
              >
                <Icone.Relogio />
                {simulando === "SENATRAN_24H"
                  ? "Gravando…"
                  : "Simular 24 h vencendo"}
              </Botao>
              <Botao
                papel="atencao"
                disabled={simulando !== null}
                title="Demonstração: grava um trânsito em processamento há quase 7 dias. O cancelamento acontece de verdade, pelo motor de prazos."
                onClick={() => void simularPrazo("SENATRAN_7D")}
              >
                <Icone.Relogio />
                {simulando === "SENATRAN_7D"
                  ? "Gravando…"
                  : "Simular 7 dias vencendo"}
              </Botao>
            </>
          )}
          {podeSimular && (
            <Botao
              aria-expanded={verRoteiro}
              aria-controls="roteiro-da-demonstracao"
              title="Os dez cenários de homologação, com o passo a passo, e quais já aconteceram nesta organização."
              onClick={() => definirVerRoteiro((v) => !v)}
            >
              <Icone.Certo />
              Roteiro da demonstração
            </Botao>
          )}
          {supervisiona(quem?.perfil) && (
            // A lista de isentos (`A-06`): da supervisão, e não da mesa do operador — ele vê
            // o resultado em cada trânsito, com a fonte.
            <Link
              to="/senatran/isentos"
              title="As placas que a concessionária isenta: incluir, remover e importar a planilha."
              className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
            >
              <Icone.Lista />
              Lista de isentos
            </Link>
          )}
          {/* A caixa das notificações internas (`R-08`): de quem as recebe na tela. */}
          <LinkDasNotificacoes />
          <Botao
            aria-expanded={verRegime}
            aria-controls="regime-de-prazos"
            title="De onde cada prazo conta, quanto dura e de onde vem a regra, por concessão."
            onClick={() => definirVerRegime((v) => !v)}
          >
            <Icone.Relogio />
            Regime de prazos
          </Botao>
          <span className="inline-flex items-center gap-1.5 rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-[11px] text-tinta-3">
            <Icone.Recarga />
            {fila.atualizadoEm
              ? `Atualizado às ${fila.atualizadoEm}`
              : "Carregando…"}
          </span>
        </div>
      </div>

      {/* Um bloco só para filtro, aviso e régua, como na Operação (21/09): a moldura divide a
          tela em três linhas (título, isto, trabalho), e um aviso solto virava uma quarta — a
          tabela caía numa linha sem altura e sumia (Gisele, 25/09, com "TRK2A01: Em
          processamento" na tela). */}
      <div className="grid gap-2">
        {verRoteiro && podeSimular && (
          <RoteiroDaDemonstracao atualizadoEm={fila.atualizadoEm} />
        )}
        {verRegime && <RegimeDePrazos />}
        {recorteDeIndicadores && (
          <div className="flex flex-wrap items-center gap-2 rounded-cartao border border-azul-borda bg-azul-fundo px-3 py-2">
            <Badge cor="azul">
              {ORIGEM_DO_RECORTE[recorteDeIndicadores] ??
                "recorte de Indicadores"}
            </Badge>
            <span className="text-[11.5px] text-tinta-2">
              {ROTULO_DO_RECORTE[recorteDeIndicadores] ?? recorteDeIndicadores}
              {parametros.get("desde") && " · no período escolhido lá"} — inclui
              o que não passou pela aprovação.
            </span>
            <Botao onClick={() => definirParametros({})}>
              <Icone.Barrado />
              Remover o filtro
            </Botao>
          </div>
        )}

        {aviso && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-2 rounded-cartao border border-verde-borda bg-verde-fundo px-3 py-2"
          >
            <span className="text-verde">
              <Icone.Certo />
            </span>
            <span className="text-[11.5px] text-tinta-2">{aviso}</span>
            <Botao onClick={() => definirAviso(null)}>Fechar</Botao>
          </div>
        )}

        {/* Uma linha só a partir de `md` (Gisele, 25/09): uma coluna por cartão, do mesmo
          tamanho, e o `Kpi` compacto para o rótulo não quebrar. */}
        <div className="grid grid-cols-2 gap-2 md:auto-cols-[minmax(0,1fr)] md:grid-flow-col md:grid-cols-none">
          {CARTOES_DA_SENATRAN.map((c) => (
            <Kpi
              compacto
              key={c.chave}
              rotulo={c.rotulo}
              valor={regua ? (regua[c.chave] ?? 0) : null}
              icone={c.icone}
              {...(c.severidade ? { severidade: c.severidade } : {})}
              ativo={cartao === c.chave}
              aoClicar={() => acender(c.chave)}
              detalhe={detalheDoPrazo(
                c.chave,
                regua?.[c.chave] ?? null,
                proximos,
              )}
            />
          ))}
        </div>
      </div>

      {/* `md:min-h-[360px]`: num notebook baixo a tabela e o detalhe ficavam com a altura de
          uma linha. Abaixo disto a página rola — a régua sai de vista, mas o trabalho não
          some (a moldura rola só quando não cabe). */}
      <div className="grid min-h-0 grid-cols-1 gap-3 md:min-h-[360px] xl:grid-cols-[minmax(0,1fr)_340px] 2xl:grid-cols-[minmax(0,1fr)_380px]">
        <Painel
          rolagemPropria
          titulo="Trânsitos validados"
          acoes={
            <span
              className="flex flex-wrap gap-1"
              role="group"
              aria-label="Filtros"
            >
              {[
                ...ABAS,
                ...ABAS_DA_CONTINGENCIA.filter(
                  (a) =>
                    a.chave === cartao ||
                    (regua?.[a.chave] ?? 0) > 0 ||
                    (a.chave === "na_fila_da_contingencia" &&
                      (contingencia?.na_fila ?? 0) > 0),
                ),
              ].map((a) => (
                <Chip
                  key={a.rotulo}
                  ativo={
                    a.chave !== null &&
                    (a.chave === cartao ||
                      (a.chave === "senatran" && cartao === universo))
                  }
                  desabilitado={a.chave === null}
                  titulo={
                    a.chave === null
                      ? "Depende da saga de reclassificação (R-07)"
                      : undefined
                  }
                  aoClicar={() =>
                    a.chave &&
                    acender(a.chave === "senatran" ? universo : a.chave)
                  }
                >
                  {a.rotulo}
                  {a.chave &&
                    regua?.[a.chave === "senatran" ? universo : a.chave] !=
                      null &&
                    ` · ${regua[a.chave === "senatran" ? universo : a.chave]!.toLocaleString("pt-BR")}`}
                </Chip>
              ))}
            </span>
          }
          rodape={
            <Paginacao
              pagina={pagina}
              limite={limite}
              total={fila.total}
              naPagina={itens.length}
              aoMudar={definirPagina}
              aoAtualizar={() => void fila.recarregar()}
            />
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            {!somenteLeitura && (
              <>
                <Botao
                  papel="primaria"
                  disabled={emLote || enviaveis.length === 0}
                  onClick={() => void emMassa("enviar")}
                >
                  <Icone.Enviar />
                  {andamento?.tipo === "enviar"
                    ? `Processando ${andamento.feitos} de ${andamento.total}…`
                    : `Enviar selecionados (${enviaveis.length})`}
                </Botao>
                <Botao
                  disabled={emLote || consultaveis.length === 0}
                  onClick={() => void emMassa("consultar")}
                >
                  <Icone.Recarga />
                  {andamento?.tipo === "consultar"
                    ? `Consultando ${andamento.feitos} de ${andamento.total}…`
                    : `Atualizar status (${consultaveis.length})`}
                </Botao>
                {/* O-09: o filtro inteiro, gerado no servidor — não a página. Link, e não fetch:
                o navegador baixa em fluxo, com o cookie de leitura. */}
                <a
                  href={exportacao("csv")}
                  aria-disabled={fila.total === 0}
                  // `aria-disabled` só avisa a leitor de tela: o link continuava clicável e
                  // baixava um arquivo vazio.
                  onClick={(e) => {
                    if (fila.total === 0) e.preventDefault();
                  }}
                  className={`inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel px-2.5 text-[11.5px] font-semibold text-tinta hover:bg-painel-2 ${
                    fila.total === 0 ? "opacity-45" : ""
                  }`}
                  title="Tudo o que o filtro e o cartão selecionam, e não só esta página"
                >
                  <Icone.Baixar />
                  Exportar CSV
                  {fila.total != null &&
                    ` (${fila.total.toLocaleString("pt-BR")})`}
                </a>
                <a
                  href={exportacao("json")}
                  className="text-[11px] text-tinta-3 underline hover:text-tinta"
                >
                  JSON
                </a>
              </>
            )}
            <form
              role="search"
              className="ml-auto flex items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                buscar(digitada);
              }}
            >
              <input
                aria-label="Buscar placa, id ou protocolo"
                placeholder="Placa, id ou protocolo"
                value={digitada}
                onChange={(e) => definirDigitada(e.target.value)}
                className="min-h-8 w-52 rounded-botao border border-linha bg-painel px-2 text-[11.5px]"
              />
              <Botao type="submit">
                <Icone.Lupa />
                Buscar
              </Botao>
              {busca && (
                <Botao type="button" onClick={() => buscar("")}>
                  Limpar
                </Botao>
              )}
            </form>
            {lote && (
              <span
                role="status"
                className={lote.cor === "verde" ? "text-verde" : "text-amarelo"}
              >
                {lote.texto}
              </span>
            )}
          </div>

          {fila.erro ? (
            <Estado
              tipo={fila.erro.semLicenca ? "sem-licenca" : "erro"}
              titulo={
                fila.erro.semLicenca
                  ? "Esta organização não licenciou o módulo TollVision."
                  : "Não foi possível carregar a fila."
              }
              detalhe={fila.erro.message}
            />
          ) : fila.itens === null ? (
            <Estado tipo="carregando" titulo="Carregando os trânsitos…" />
          ) : itens.length === 0 ? (
            <Estado
              tipo="vazio"
              titulo="Nenhum trânsito neste filtro."
              detalhe={
                somenteLeitura
                  ? "Só aparece aqui o que a Operação aprovou. Troque o cartão."
                  : "Só aparece aqui o que a Operação aprovou. Troque o cartão ou confira a fila da Operação."
              }
            />
          ) : celular ? (
            // No celular, um cartão por trânsito (`C-17` fase 2): nove colunas numa tela de
            // 390 px eram uma faixa de 63 px que rolava para os dois lados. Tocar abre o
            // detalhe, que fica logo abaixo — e a página desce até ele.
            <ul aria-label="Trânsitos validados" className="grid gap-1.5">
              {itens.map((t) => (
                <CartaoDoTransito
                  key={t.id}
                  transito={t}
                  marcavel={!somenteLeitura}
                  marcado={marcados.has(t.id)}
                  selecionado={t.id === selecionado}
                  aoMarcar={() => marcar(t.id)}
                  aoSelecionar={() => {
                    definirSelecionado(t.id);
                    document
                      .getElementById("detalhe-do-transito")
                      ?.scrollIntoView?.({
                        behavior: "smooth",
                        block: "start",
                      });
                  }}
                />
              ))}
            </ul>
          ) : (
            // A tabela rola sozinha, nos dois sentidos, com o cabeçalho parado: a barra
            // horizontal fica sempre à vista, no pé do painel (Gisele, 25/09).
            <div className="min-h-0 overflow-auto">
              <table
                aria-label="Trânsitos validados"
                className="w-max min-w-full text-left text-[11.5px] whitespace-nowrap [&_td]:pr-3 [&_th]:pr-3"
              >
                <thead className="sticky top-0 z-10 bg-painel text-[10px] tracking-[0.06em] text-tinta-3 uppercase">
                  <tr className="border-b border-linha">
                    {!somenteLeitura && (
                      <th className="w-8 py-1.5">
                        <input
                          type="checkbox"
                          aria-label="Marcar a página"
                          checked={todosMarcados}
                          onChange={() =>
                            definirMarcados(
                              todosMarcados
                                ? new Set()
                                : new Set(itens.map((t) => t.id)),
                            )
                          }
                        />
                      </th>
                    )}
                    <CabecalhoOrdenavel
                      coluna="placa"
                      rotulo="Placa"
                      ordem={ordem}
                      direcao={direcao}
                      aoOrdenar={ordenar}
                    />
                    <CabecalhoOrdenavel
                      coluna="passagem"
                      rotulo="Passagem"
                      ordem={ordem}
                      direcao={direcao}
                      aoOrdenar={ordenar}
                    />
                    <CabecalhoOrdenavel
                      coluna="envio"
                      rotulo="Envio SENATRAN"
                      ordem={ordem}
                      direcao={direcao}
                      aoOrdenar={ordenar}
                    />
                    <th>Status SERPRO</th>
                    <th>TAG</th>
                    <th className="text-right">Tent.</th>
                    <th>Próximo prazo</th>
                    <th>Situação do trânsito</th>
                    <th>Ação necessária</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((t) => (
                    <LinhaDaTabela
                      key={t.id}
                      transito={t}
                      marcavel={!somenteLeitura}
                      marcado={marcados.has(t.id)}
                      selecionado={t.id === selecionado}
                      aoMarcar={() => marcar(t.id)}
                      aoSelecionar={() => definirSelecionado(t.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Painel>

        <div id="detalhe-do-transito" className="grid min-h-0 scroll-mt-16">
          <DetalheDoTransito
            detalhe={transito.detalhe}
            trilha={transito.trilha}
            carregando={transito.carregando}
            erro={transito.erro}
            aoMudar={async (texto) => {
              if (texto) definirAviso(texto);
              await Promise.all([transito.recarregar(), fila.recarregar()]);
            }}
          />
        </div>
      </div>
    </>
  );
}

/** O trânsito em cartão, no celular: a placa e a situação em cima, o envio e o prazo
 *  embaixo — as colunas que decidem o que fazer, na ordem em que se lê. */
function CartaoDoTransito({
  transito: t,
  marcavel = true,
  marcado,
  selecionado,
  aoMarcar,
  aoSelecionar,
}: {
  transito: ItemDaFila;
  marcavel?: boolean;
  marcado: boolean;
  selecionado: boolean;
  aoMarcar: () => void;
  aoSelecionar: () => void;
}) {
  const status = APRESENTACAO_DO_STATUS[t.status];
  const envio = APRESENTACAO_DO_ENVIO[t.situacao_envio];
  const urgencia = urgenciaDoPrazo(t.proximo_prazo_tipo, t.proximo_prazo_em);
  return (
    <li
      className={`grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2.5 rounded-cartao border px-2.5 py-2 ${
        selecionado ? "border-azul bg-realce" : "border-linha bg-painel-2"
      }`}
    >
      {marcavel ? (
        <input
          type="checkbox"
          aria-label={`Marcar ${t.placa ?? t.id}`}
          checked={marcado}
          onChange={aoMarcar}
          className="mt-1 size-5"
        />
      ) : (
        <span />
      )}
      <button
        type="button"
        aria-current={selecionado}
        onClick={aoSelecionar}
        className="grid min-w-0 gap-1 text-left"
      >
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="mono text-[14px] font-bold text-tinta">
            {t.placa ?? "—"}
          </span>
          {ehDemonstracao(t) && <Badge cor="cinza">simulado</Badge>}
          <span className="ml-auto">
            <Badge cor={status.cor}>{status.rotulo}</Badge>
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-tinta-2">
          <span className="mono">{dataCurta(t.timestamp_passagem)}</span>
          <Badge cor={envio.cor}>{envio.rotulo}</Badge>
          {t.status_tag !== "NENHUMA" && (
            <span className="text-tinta-3">
              TAG {t.status_tag.toLowerCase()}
            </span>
          )}
        </span>
        <span className="text-[11.5px]">
          <AcaoDoTransito acao={t.acao_necessaria} />
        </span>
        {t.proximo_prazo_em && t.proximo_prazo_tipo && (
          <span className={`mono text-[11.5px] ${COR_DA_URGENCIA[urgencia]}`}>
            {`${PRAZO_CURTO[t.proximo_prazo_tipo] ?? rotuloDoPrazo(t.proximo_prazo_tipo)} · ${restante(t.proximo_prazo_em)}`}
          </span>
        )}
      </button>
    </li>
  );
}

function LinhaDaTabela({
  transito: t,
  marcavel = true,
  marcado,
  selecionado,
  aoMarcar,
  aoSelecionar,
}: {
  transito: ItemDaFila;
  marcavel?: boolean;
  marcado: boolean;
  selecionado: boolean;
  aoMarcar: () => void;
  aoSelecionar: () => void;
}) {
  const status = APRESENTACAO_DO_STATUS[t.status];
  // Tarefa 9: "Status SERPRO" é como está o **envio**, decidido no servidor; o status da
  // cobrança (Pago, Pendente…) foi para a última coluna, "Situação do trânsito".
  const envio = APRESENTACAO_DO_ENVIO[t.situacao_envio];
  return (
    <tr
      aria-current={selecionado}
      onClick={aoSelecionar}
      className={`cursor-pointer border-b border-linha-suave ${
        selecionado ? "bg-realce" : "hover:bg-painel-2"
      }`}
    >
      {marcavel && (
        <td
          className={`py-1.5 ${FILETE[String(selecionado) as "true" | "false"]}`}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            aria-label={`Marcar ${t.placa ?? t.id}`}
            checked={marcado}
            onChange={aoMarcar}
          />
        </td>
      )}
      <td
        className={`mono font-bold text-tinta ${marcavel ? "" : FILETE[String(selecionado) as "true" | "false"]}`}
      >
        {/* O teclado: a linha só abria com o mouse (`<tr onClick>`); a placa é o botão que a
            abre — Tab chega, Enter e Espaço abrem (`N250`). */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            aoSelecionar();
          }}
          aria-label={`Abrir o envio de ${t.placa ?? t.id}`}
          className="mono rounded-botao text-left font-bold text-tinta hover:underline"
        >
          {t.placa ?? "—"}
        </button>
        {ehDemonstracao(t) && (
          <span className="ml-1">
            <Badge cor="cinza">simulado</Badge>
          </span>
        )}
      </td>
      <td className="mono text-tinta-2">{dataCurta(t.timestamp_passagem)}</td>
      <td className="mono text-tinta-2">
        {t.enviado_em ? dataCurta(t.enviado_em) : "—"}
      </td>
      <td>
        {/* O motivo da recusa inteiro no detalhe; aqui, ao passar o mouse. */}
        <span title={t.motivo_senatran ?? undefined}>
          <Badge cor={envio.cor}>{envio.rotulo}</Badge>
        </span>
      </td>
      <td className="text-tinta-2">
        {t.status_tag === "NENHUMA" ? "sem TAG" : t.status_tag.toLowerCase()}
      </td>
      <td className="mono text-right text-tinta-2">{t.tentativas}</td>
      <td
        className={`mono ${COR_DA_URGENCIA[urgenciaDoPrazo(t.proximo_prazo_tipo, t.proximo_prazo_em)]}`}
      >
        {t.proximo_prazo_em && t.proximo_prazo_tipo ? (
          <span className="inline-flex items-center gap-1">
            {urgenciaDoPrazo(t.proximo_prazo_tipo, t.proximo_prazo_em) ===
              "critico" && <Icone.Alerta />}
            {`${PRAZO_CURTO[t.proximo_prazo_tipo] ?? rotuloDoPrazo(t.proximo_prazo_tipo)} · ${restante(t.proximo_prazo_em)}`}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td>
        <Badge cor={status.cor}>{status.rotulo}</Badge>
      </td>
      <td>
        <AcaoDoTransito acao={t.acao_necessaria} />
      </td>
    </tr>
  );
}

/** O filete azul de 2 px da linha selecionada (`10-design-system.md` §3); o transparente mantém
 *  a coluna no lugar quando ela não está. */
const FILETE = {
  true: "border-l-2 border-l-azul",
  false: "border-l-2 border-l-transparent",
} as const;

/**
 * O que o trânsito espera agora (o AC03 do cronograma), decidido no servidor. A que pede
 * alguém vai em badge; a de espera, em texto discreto — cor em toda linha não é alerta.
 */
function AcaoDoTransito({ acao }: { acao: ItemDaFila["acao_necessaria"] }) {
  const apresentacao = APRESENTACAO_DA_ACAO[acao];
  return apresentacao.cor === "cinza" ? (
    <span className="text-tinta-3">{apresentacao.rotulo}</span>
  ) : (
    <Badge cor={apresentacao.cor}>{apresentacao.rotulo}</Badge>
  );
}

/**
 * A urgência de um prazo, para a cor da coluna (22/09: "ver melhor o prazo crítico"). Os
 * limiares são os dos cartões: 24 h crítico abaixo de 4 h, 7 dias crítico abaixo de 1 dia.
 * Atenção é o dobro disso. O resto fica na cor de sempre — cor em toda linha não é alerta.
 */
function urgenciaDoPrazo(
  tipo: string | null | undefined,
  vence: string | null | undefined,
  agora: Date = agoraDoInquilino(),
): "vencido" | "critico" | "atencao" | "folga" {
  if (!tipo || !vence) return "folga";
  const horas = (new Date(vence).getTime() - agora.getTime()) / 3_600_000;
  if (horas <= 0) return "vencido";
  const critico =
    tipo === "ENVIO_24H" ? 4 : tipo === "PROCESSAMENTO_7D" ? 24 : 0;
  if (horas < critico) return "critico";
  if (horas < critico * 2) return "atencao";
  return "folga";
}

const COR_DA_URGENCIA = {
  vencido: "font-bold text-vermelho",
  critico: "font-bold text-vermelho",
  atencao: "font-semibold text-amarelo",
  folga: "text-tinta-2",
} as const;
